import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  ArrowUpRight,
  Loader2,
  Flag,
  Send,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

// ---------------------------------------------------------------------------
// SpillTeaCompanyPage — company detail with tabbed reviews
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'reviews', label: 'Reviews' },
  { key: 'interview', label: 'Interview Questions' },
  { key: 'tea', label: 'Spilled Milk Tea' },
];

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const SpillTeaCompanyPage = () => {
  const navigate = useNavigate();
  const { companyDomain } = useParams();
  const { user, loading } = useAuth();
  const domain = decodeURIComponent(companyDomain || '');

  // Data
  const [reviews, setReviews] = useState([]);
  const [comments, setComments] = useState([]);
  const [votes, setVotes] = useState({}); // { targetId: { up: N, down: N } }
  const [userVotes, setUserVotes] = useState({}); // { targetId: 'up' | 'down' }
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reviews');

  // Company aggregate
  const [companyName, setCompanyName] = useState('');
  const [avgRating, setAvgRating] = useState(0);

  // Comment input
  const [inputText, setInputText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // { id, preview }
  const [submittingComment, setSubmittingComment] = useState(false);
  const inputRef = useRef(null);

  // Report
  const [reportedIds, setReportedIds] = useState(new Set());
  const [reportToast, setReportToast] = useState(null);

  // Auth redirect
  useEffect(() => {
    if (!loading && !user) navigate('/connect-email');
  }, [user, loading, navigate]);

  // Load reviews
  useEffect(() => {
    const load = async () => {
      if (!user || !domain) return;
      try {
        const { data, error } = await supabase
          .from('spill_tea_reviews')
          .select('*')
          .eq('company_domain', domain)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setReviews(data || []);
        if (data && data.length > 0) {
          setCompanyName(data[0].company_name);
          setAvgRating(Math.round(data.reduce((s, r) => s + r.boba_rating, 0) / data.length));
        }
      } catch (err) {
        console.error('Error loading reviews:', err);
      } finally {
        setIsLoading(false);
      }
    };
    if (user) load();
  }, [user, domain]);

  // Load comments
  useEffect(() => {
    const loadComments = async () => {
      if (!user || !domain) return;
      try {
        const { data, error } = await supabase
          .from('spill_tea_comments')
          .select('*')
          .eq('company_domain', domain)
          .order('created_at', { ascending: false });
        if (error) {
          console.error('Error loading comments:', error);
          return;
        }
        setComments(data || []);
      } catch (err) {
        console.error('Error loading comments:', err);
      }
    };
    if (user) loadComments();
  }, [user, domain]);

  // Load votes
  useEffect(() => {
    const loadVotes = async () => {
      if (!user || !domain) return;
      try {
        const { data, error } = await supabase
          .from('spill_tea_votes')
          .select('*')
          .eq('company_domain', domain);
        if (error || !data) return;

        const agg = {};
        const mine = {};
        data.forEach((v) => {
          const tid = v.target_id;
          if (!agg[tid]) agg[tid] = { up: 0, down: 0 };
          if (v.vote_type === 1) agg[tid].up += 1;
          else agg[tid].down += 1;
          if (v.user_id === user.id) mine[tid] = v.vote_type === 1 ? 'up' : 'down';
        });
        setVotes(agg);
        setUserVotes(mine);
      } catch { /* table may not exist */ }
    };
    if (user) loadVotes();
  }, [user, domain]);

  // Vote handler — works for any target_id (comment or review)
  const handleVote = useCallback(async (targetId, targetType, type) => {
    if (!user || !targetId) return;
    const currentVote = userVotes[targetId];

    // Optimistic UI
    if (currentVote === type) {
      setUserVotes((prev) => { const n = { ...prev }; delete n[targetId]; return n; });
      setVotes((prev) => {
        const n = { ...prev };
        if (n[targetId]) n[targetId] = { ...n[targetId], [type]: Math.max(0, n[targetId][type] - 1) };
        return n;
      });
      try {
        await supabase.from('spill_tea_votes').delete().eq('target_id', targetId).eq('user_id', user.id);
      } catch { /* ignore */ }
      return;
    }

    setUserVotes((prev) => ({ ...prev, [targetId]: type }));
    setVotes((prev) => {
      const n = { ...prev };
      if (!n[targetId]) n[targetId] = { up: 0, down: 0 };
      n[targetId] = { ...n[targetId], [type]: n[targetId][type] + 1 };
      if (currentVote) n[targetId] = { ...n[targetId], [currentVote]: Math.max(0, n[targetId][currentVote] - 1) };
      return n;
    });

    try {
      if (currentVote) {
        await supabase.from('spill_tea_votes').delete().eq('target_id', targetId).eq('user_id', user.id);
      }
      await supabase.from('spill_tea_votes').insert({
        target_id: targetId,
        target_type: targetType,
        company_domain: domain,
        user_id: user.id,
        vote_type: type === 'up' ? 1 : -1,
      });
    } catch (err) {
      console.error('Vote error:', err);
    }
  }, [user, domain, userVotes]);

  // Post comment or reply
  const handlePost = useCallback(async () => {
    if (!inputText.trim() || !user || submittingComment) return;
    setSubmittingComment(true);
    try {
      const { data, error } = await supabase
        .from('spill_tea_comments')
        .insert({
          company_domain: domain,
          user_id: user.id,
          parent_id: replyingTo ? replyingTo.id : null,
          content: inputText.trim(),
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        setReportToast('Could not post comment. Please try again.');
        setTimeout(() => setReportToast(null), 2500);
        return;
      }

      setComments((prev) => [data, ...prev]);
      setInputText('');
      setReplyingTo(null);
      if (inputRef.current) inputRef.current.style.height = 'auto';
    } catch (err) {
      console.error('Error posting comment:', err);
      setReportToast('Could not post comment. Please try again.');
      setTimeout(() => setReportToast(null), 2500);
    } finally {
      setSubmittingComment(false);
    }
  }, [inputText, user, domain, replyingTo, submittingComment]);

  // Report
  const handleReport = useCallback(async (type, id) => {
    if (reportedIds.has(`${type}-${id}`)) return;
    try {
      await supabase.from('spill_tea_reports').insert({
        reporter_id: user.id,
        reported_type: type,
        reported_id: id,
        company_domain: domain,
      });
      setReportedIds((prev) => new Set(prev).add(`${type}-${id}`));
      setReportToast("Reported \u2014 we'll review this shortly.");
      setTimeout(() => setReportToast(null), 2500);
    } catch {
      setReportToast('Could not submit report. Try again later.');
      setTimeout(() => setReportToast(null), 2500);
    }
  }, [user, domain, reportedIds]);

  // Organise comments
  const topLevelComments = comments.filter((c) => !c.parent_id);
  const repliesMap = comments.reduce((acc, c) => {
    if (c.parent_id) {
      if (!acc[c.parent_id]) acc[c.parent_id] = [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {});

  // Group interview questions by reviewer
  const interviewByReviewer = reviews
    .filter((r) => (r.interview_questions || []).some(Boolean))
    .map((r) => ({
      reviewId: r.id,
      position: r.position_title,
      questions: (r.interview_questions || []).filter(Boolean),
    }));

  // Unified tea feed
  const teaFromReviews = reviews
    .filter((r) => r.spilled_milk_tea)
    .map((r) => ({
      id: r.id,
      type: 'review-tea',
      content: r.spilled_milk_tea,
      created_at: r.created_at,
      targetType: 'review',
    }));

  const teaFromComments = topLevelComments.map((c) => ({
    id: c.id,
    type: 'comment',
    content: c.content,
    created_at: c.created_at,
    targetType: 'comment',
  }));

  const teaFeed = [...teaFromReviews, ...teaFromComments].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  // Reply helpers
  const startReply = (commentId, preview) => {
    setReplyingTo({ id: commentId, preview: preview.slice(0, 50) + (preview.length > 50 ? '...' : '') });
    setInputText('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const cancelReply = () => setReplyingTo(null);

  if (loading || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden pb-16" style={{ position: 'relative', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url(/bluewhite.png)', backgroundSize: 'cover', backgroundPosition: 'center', zIndex: -1, transform: 'translateZ(0)', willChange: 'transform' }} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;900&family=Inter:wght@400;500;600;700&display=swap');

        .tab-pill { transition: all 0.25s cubic-bezier(0.4,0,0.2,1); }

        .review-card {
          background: linear-gradient(145deg, rgba(200,210,220,0.4) 0%, rgba(180,195,210,0.3) 50%, rgba(170,185,200,0.25) 100%);
          border: 1px solid rgba(255,255,255,0.4);
          backdrop-filter: blur(14px) saturate(150%);
          -webkit-backdrop-filter: blur(14px) saturate(150%);
          box-shadow: 0 6px 24px -6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.3);
          transition: all 0.2s ease;
        }
        .review-card:hover .report-btn { opacity: 1; }
        .report-btn { opacity: 0; transition: opacity 0.2s ease; }

        .tea-bubble {
          background: linear-gradient(145deg, rgba(200,210,220,0.35) 0%, rgba(180,195,210,0.25) 100%);
          border: 1px solid rgba(255,255,255,0.35);
          backdrop-filter: blur(12px) saturate(140%);
          -webkit-backdrop-filter: blur(12px) saturate(140%);
          box-shadow: 0 4px 16px -4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.25);
          transition: all 0.15s ease;
        }
        .tea-bubble:hover { background: linear-gradient(145deg, rgba(210,218,228,0.45) 0%, rgba(195,208,220,0.35) 100%); }

        .reply-thread-card {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .vote-arrow {
          display: inline-flex; align-items: center; gap: 2px;
          font-size: 11px;
          padding: 2px 5px; border-radius: 6px;
          transition: all 0.12s ease;
          font-family: 'Inter', sans-serif;
          cursor: pointer; border: none; background: none;
          line-height: 1;
        }
        .vote-arrow:hover { background: rgba(255,255,255,0.15); }
        .vote-arrow svg { width: 12px; height: 12px; }
      `}</style>

      {/* Header */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-xl"
        style={{
          background: 'rgba(255,252,248,0.65)',
          borderColor: 'rgba(255,255,255,0.3)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.08)',
          WebkitBackdropFilter: 'blur(20px)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/spill-tea/home')} className="rounded-xl shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '1.3rem' }}>🧋</span>
              <h1 className="text-lg font-bold truncate" style={{ fontFamily: "'Montserrat', sans-serif", color: '#2d1b0e' }}>
                {companyName || domain}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-6">

        {/* Company Hero */}
        <section className="mb-8">
          <h2
            className="text-3xl sm:text-4xl font-black tracking-tight mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif", color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.15)' }}
          >
            {companyName || domain}
          </h2>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex gap-1.5">
              {Array.from({ length: 10 }, (_, i) => (
                <div key={i} className="rounded-full" style={{ width: 14, height: 14, background: i < avgRating ? 'linear-gradient(145deg, #5C3A1E, #8B6914)' : 'rgba(255,255,255,0.2)', border: i < avgRating ? '1.5px solid rgba(139,105,20,0.5)' : '1.5px solid rgba(255,255,255,0.15)', boxShadow: i < avgRating ? 'inset 0 -2px 4px rgba(0,0,0,0.2), 0 1px 4px rgba(92,58,30,0.2)' : 'none' }} />
              ))}
            </div>
            <span className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.8)', fontFamily: "'Inter', sans-serif" }}>{avgRating}/10</span>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}>({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})</span>
          </div>
          {domain && (
            <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold transition-all hover:gap-2.5" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Inter', sans-serif" }}>
              Website <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </section>

        {/* Tab bar */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="tab-pill shrink-0 px-5 py-2.5 rounded-2xl text-sm font-semibold"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.65)',
                  background: isActive ? 'linear-gradient(135deg, #1e3a5f, #2563eb)' : 'rgba(255,255,255,0.15)',
                  border: isActive ? 'none' : '1px solid rgba(255,255,255,0.25)',
                  boxShadow: isActive ? '0 4px 16px rgba(37,99,235,0.3)' : 'none',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">

          {/* REVIEWS */}
          {activeTab === 'reviews' && (
            <motion.div key="reviews" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }} className="space-y-4">
              {reviews.length === 0 ? (
                <EmptyState message="No reviews yet for this company." />
              ) : (
                reviews.map((review) => (
                  <div key={review.id} className="review-card rounded-2xl p-5 relative group">
                    <button className="report-btn absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/15 transition-colors" title="Report" onClick={() => handleReport('review', review.id)}>
                      <Flag className="w-4 h-4" style={{ color: reportedIds.has(`review-${review.id}`) ? '#ef4444' : 'rgba(255,255,255,0.35)' }} />
                    </button>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.9)', fontFamily: "'Inter', sans-serif" }}>{review.position_title}</span>
                      {review.duration && (
                        <span className="text-xs px-2.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', fontFamily: "'Inter', sans-serif" }}>{review.duration}</span>
                      )}
                    </div>
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: 10 }, (_, i) => (
                        <div key={i} className="rounded-full" style={{ width: 8, height: 8, background: i < review.boba_rating ? 'linear-gradient(145deg, #5C3A1E, #8B6914)' : 'rgba(255,255,255,0.15)', border: i < review.boba_rating ? '1px solid rgba(139,105,20,0.4)' : '1px solid rgba(255,255,255,0.1)' }} />
                      ))}
                      <span className="text-xs font-semibold ml-1" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}>{review.boba_rating}/10</span>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#1a1a1a', fontFamily: "'Inter', sans-serif" }}>{review.job_experience}</p>
                  </div>
                ))
              )}
            </motion.div>
          )}

          {/* INTERVIEW QUESTIONS */}
          {activeTab === 'interview' && (
            <motion.div key="interview" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }} className="space-y-6">
              {interviewByReviewer.length === 0 ? (
                <EmptyState message="No interview questions shared yet." />
              ) : (
                interviewByReviewer.map((group) => (
                  <div key={group.reviewId}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.2), rgba(37,99,235,0.1))', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(37,99,235,0.2)', fontFamily: "'Inter', sans-serif" }}>
                        {group.position}
                      </div>
                    </div>
                    <div className="space-y-2.5">
                      {group.questions.map((q, idx) => (
                        <div key={`${group.reviewId}-${idx}`} className="review-card rounded-2xl px-5 py-4 relative group">
                          <button className="report-btn absolute top-3.5 right-4 p-1.5 rounded-lg hover:bg-white/15 transition-colors" title="Report" onClick={() => handleReport('review', group.reviewId)}>
                            <Flag className="w-3.5 h-3.5" style={{ color: reportedIds.has(`review-${group.reviewId}`) ? '#ef4444' : 'rgba(255,255,255,0.3)' }} />
                          </button>
                          <div className="flex items-start gap-3">
                            <span className="text-xs font-bold shrink-0 mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', fontFamily: "'Inter', sans-serif" }}>Q</span>
                            <p className="text-sm leading-relaxed" style={{ color: '#1a1a1a', fontFamily: "'Inter', sans-serif" }}>{q}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          )}

          {/* SPILLED MILK TEA */}
          {activeTab === 'tea' && (
            <motion.div key="tea" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>

              {/* Composer at the top */}
              <div className="mb-6">
                <AnimatePresence>
                  {replyingTo && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)' }}>
                        <div className="w-0.5 h-5 rounded-full shrink-0" style={{ background: '#2563eb' }} />
                        <span className="text-xs truncate flex-1" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: "'Inter', sans-serif" }}>
                          Replying to: <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>{replyingTo.preview}</span>
                        </span>
                        <button onClick={cancelReply} className="p-1 rounded-lg hover:bg-white/10 transition-colors shrink-0">
                          <X className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.5)' }} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div
                  className="relative flex items-end rounded-[22px] overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.82)', border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 4px 20px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                >
                  <textarea
                    ref={inputRef}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={replyingTo ? 'Write a reply...' : 'Spill some tea...'}
                    rows={1}
                    className="flex-1 py-3 pl-4 pr-2 bg-transparent outline-none resize-none text-sm"
                    style={{ fontFamily: "'Inter', sans-serif", color: '#1a1a1a', maxHeight: '120px', lineHeight: '1.5' }}
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); }
                      if (e.key === 'Escape' && replyingTo) cancelReply();
                    }}
                  />
                  <button
                    onClick={handlePost}
                    disabled={!inputText.trim() || submittingComment}
                    className="shrink-0 p-2 mr-1.5 mb-1.5 rounded-full transition-all duration-150"
                    style={{ background: inputText.trim() ? 'linear-gradient(135deg, #1e3a5f, #2563eb)' : 'rgba(0,0,0,0.06)', cursor: inputText.trim() ? 'pointer' : 'default' }}
                  >
                    {submittingComment ? (
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: inputText.trim() ? '#fff' : '#9ca3af' }} />
                    ) : (
                      <Send className="w-4 h-4" style={{ color: inputText.trim() ? '#fff' : '#9ca3af' }} />
                    )}
                  </button>
                </div>
              </div>

              {/* Feed */}
              <div className="space-y-3">
                {teaFeed.length === 0 && <EmptyState message="No tea spilled yet. Be the first!" />}
                {teaFeed.map((item) => (
                  <TeaItem
                    key={item.id}
                    item={item}
                    votes={votes}
                    userVotes={userVotes}
                    onVote={handleVote}
                    onReport={handleReport}
                    reportedIds={reportedIds}
                    replies={item.type === 'comment' ? (repliesMap[item.id] || []) : []}
                    startReply={startReply}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Toast */}
      <AnimatePresence>
        {reportToast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-sm font-semibold"
            style={{ background: 'rgba(30,30,30,0.9)', color: '#fff', fontFamily: "'Inter', sans-serif", backdropFilter: 'blur(12px)', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}
          >
            {reportToast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TeaItem — single row: [hover timestamp left] | [compact bubble with text + ▲ ▼ 🚩]
// ---------------------------------------------------------------------------

const ARROW_DEFAULT = 'rgba(255,255,255,0.65)';
const ARROW_ACTIVE = '#fff';

const UpArrow = ({ bold }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={bold ? 3 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 12V4M4 7l4-3 4 3" />
  </svg>
);
const DownArrow = ({ bold }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={bold ? 3 : 2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 4v8M4 9l4 3 4-3" />
  </svg>
);

const TeaItem = ({ item, votes, userVotes, onVote, onReport, reportedIds, replies, startReply }) => {
  const [showReplies, setShowReplies] = useState(false);
  const v = votes[item.id] || { up: 0, down: 0 };
  const myVote = userVotes[item.id] || null;
  const reported = item.targetType === 'review'
    ? reportedIds.has(`review-${item.id}`)
    : reportedIds.has(`comment-${item.id}`);

  // For review-tea items, reply creates a top-level comment (parent_id = null)
  const replyId = item.type === 'comment' ? item.id : null;

  return (
    <div>
      {/* Bubble — full width */}
      <div className="tea-bubble rounded-2xl px-4 py-3 flex items-center gap-3">
        <p
          className="text-sm leading-snug whitespace-pre-wrap flex-1 min-w-0"
          style={{ color: '#1a1a1a', fontFamily: "'Inter', sans-serif" }}
        >
          {item.content}
        </p>

        {/* ▲ count  ▼ count  🚩 */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            className="vote-arrow"
            style={{ color: myVote === 'up' ? ARROW_ACTIVE : ARROW_DEFAULT, fontWeight: myVote === 'up' ? 900 : 600 }}
            onClick={() => onVote(item.id, item.targetType, 'up')}
          >
            <UpArrow bold={myVote === 'up'} />
            <span>{v.up}</span>
          </button>
          <button
            className="vote-arrow"
            style={{ color: myVote === 'down' ? ARROW_ACTIVE : ARROW_DEFAULT, fontWeight: myVote === 'down' ? 900 : 600 }}
            onClick={() => onVote(item.id, item.targetType, 'down')}
          >
            <DownArrow bold={myVote === 'down'} />
            <span>{v.down}</span>
          </button>
          <button
            className="vote-arrow"
            style={{ color: reported ? '#ef4444' : 'rgba(255,255,255,0.4)' }}
            onClick={() => onReport(item.targetType === 'review' ? 'review' : 'comment', item.id)}
            title="Report"
          >
            <Flag style={{ width: 12, height: 12 }} />
          </button>
        </div>
      </div>

      {/* Reply + thread controls — shown on ALL items */}
      <div className="flex items-center gap-3 mt-1 ml-1">
        {startReply && (
          <button
            className="text-[11px] font-bold px-3 py-1 rounded-full transition-all hover:scale-105"
            style={{
              background: 'rgba(30, 58, 95, 0.45)',
              color: '#fff',
              fontFamily: "'Inter', sans-serif",
              border: '1px solid rgba(30, 58, 95, 0.3)',
            }}
            onClick={() => startReply(replyId, item.content)}
          >
            Reply
          </button>
        )}
        {replies.length > 0 && (
          <button
            className="text-[11px] font-semibold flex items-center gap-1 transition-colors"
            style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}
            onClick={() => setShowReplies(!showReplies)}
          >
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
            {showReplies ? <ChevronUp style={{ width: 12, height: 12 }} /> : <ChevronDown style={{ width: 12, height: 12 }} />}
          </button>
        )}
      </div>

      {/* Threaded replies */}
      <AnimatePresence>
        {showReplies && replies.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="mt-1.5 space-y-1.5 border-l-2" style={{ borderColor: 'rgba(255,255,255,0.12)', marginLeft: 8, paddingLeft: 12 }}>
              {replies.map((reply) => {
                const rv = votes[reply.id] || { up: 0, down: 0 };
                const rMyVote = userVotes[reply.id] || null;
                const rReported = reportedIds.has(`comment-${reply.id}`);
                return (
                  <div key={reply.id} className="reply-thread-card rounded-xl px-3 py-2 flex items-center gap-2">
                    <p className="text-[13px] leading-snug whitespace-pre-wrap flex-1 min-w-0" style={{ color: 'rgba(0,0,0,0.7)', fontFamily: "'Inter', sans-serif" }}>
                      {reply.content}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      <button className="vote-arrow" style={{ color: rMyVote === 'up' ? ARROW_ACTIVE : ARROW_DEFAULT, fontWeight: rMyVote === 'up' ? 900 : 600 }} onClick={() => onVote(reply.id, 'comment', 'up')}>
                        <UpArrow bold={rMyVote === 'up'} /><span>{rv.up}</span>
                      </button>
                      <button className="vote-arrow" style={{ color: rMyVote === 'down' ? ARROW_ACTIVE : ARROW_DEFAULT, fontWeight: rMyVote === 'down' ? 900 : 600 }} onClick={() => onVote(reply.id, 'comment', 'down')}>
                        <DownArrow bold={rMyVote === 'down'} /><span>{rv.down}</span>
                      </button>
                      <button className="vote-arrow" style={{ color: rReported ? '#ef4444' : 'rgba(255,255,255,0.35)' }} onClick={() => onReport('comment', reply.id)} title="Report">
                        <Flag style={{ width: 11, height: 11 }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

const EmptyState = ({ message }) => (
  <div className="text-center py-12">
    <span style={{ fontSize: '2.5rem' }}>🧋</span>
    <p className="text-sm mt-3" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}>
      {message}
    </p>
  </div>
);

export default SpillTeaCompanyPage;
